import {Trans} from '@lingui/macro';
import {chain} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';

import {FrontEvent, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {styled} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  buildTeammateDisplayName,
  doesTeammateMatchQuery,
  findParticipatingNamespaces,
} from '../../../../helpers/modelHelpers';
import {sortByBooleanMap} from '../../../../helpers/sortHelpers';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {TeammateAvatar} from '../../avatars/teammateAvatar';
import {DropdownItemTeammateAvatar} from '../dropdownItemTeammateAvatar';
import {DeprecatedSearchDropdown} from '../search/deprecatedSearchDropdown';
import {SyncDropdownSource} from '../source/syncDropdownSource';

/*
 * Props.
 */

interface PickTeammateWorkspacesDropdownBoxProps {
  namespace: string;
  teammates: ReadonlyArray<TeammateModel>;
  selectedTeammateIds: ReadonlyArray<number>;
  onSelectedTeammatesChange: HandlerOf<ReadonlyArray<number>>;
  label?: ReactNode;
  shouldHideLabel?: boolean;
}

type IsCheckedMap = Map<TeammateModel['id'], boolean>;

/*
 * Style.
 */

const StyledFadedDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  flex: 1;
  ${ellipsis()};
`;

const StyledTagDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTeammatesContainerDiv = styled.div`
  display: inline-block;
  width: 220px;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};
`;

const StyledTeammateItemsWrapperDiv = styled.div`
  display: inline-flex;
  height: 100%;
  max-width: 85%;
`;

const StyledTeammateItemDiv = styled.div`
  display: inline-block;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};

  &:first-child {
    max-width: 100%;
  }

  &:not(:first-child) {
    flex: 1;
    margin-left: 4px;
    ${ellipsis()};
  }
`;

const StyledTeammateRemainderDiv = styled.div`
  display: inline-block;
  max-width: 15%;
  margin-left: 4px;
`;

const StyledTeammateAvatarDiv = styled.div`
  display: inline-block;
  margin-bottom: -3px;
  margin-right: 8px;
`;

const StyledAllTeammatesSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};
`;

/*
 * Component.
 */

export const PickTeammateWorkspacesDropdownBox: FC<PickTeammateWorkspacesDropdownBoxProps> = (props) => {
  const namespaceTeammates = useMemo(
    () => computeNamespaceTeammates(props.teammates, props.namespace),
    [props.teammates, props.namespace],
  );

  const isCheckedMap = useMemo(
    () =>
      namespaceTeammates.reduce((currentMap, teammate) => {
        currentMap.set(computeIsCheckedMapKey(teammate), props.selectedTeammateIds.includes(teammate.id));

        return currentMap;
      }, new Map<TeammateModel['id'], boolean>()),
    [namespaceTeammates, props.selectedTeammateIds],
  );

  // When the dropdown is first opened, group checked items at the top. Then keep current ordering.
  const [teammates, setTeammates] = useState<ReadonlyArray<TeammateModel>>(() =>
    sortByBooleanMap(namespaceTeammates, isCheckedMap, computeIsCheckedMapKey),
  );
  const onPopoverToggle = useCallback(
    (shown: boolean) => {
      // Only re-sort the teammate list when the dropdown is hidden.
      if (shown) {
        return;
      }

      const newTeammates = sortByBooleanMap(namespaceTeammates, isCheckedMap, computeIsCheckedMapKey);
      setTeammates(newTeammates);
    },
    [namespaceTeammates, isCheckedMap],
  );

  return (
    <DropdownBoxCoordinator
      label={(!props.shouldHideLabel && props.label) || undefined}
      value={renderValue(teammates, props)}
      renderDropdown={(popoverProps) => renderDropdown(teammates, isCheckedMap, popoverProps, props)}
      onPopoverToggle={onPopoverToggle}
    />
  );
};

function renderValue(teammates: ReadonlyArray<TeammateModel>, props: PickTeammateWorkspacesDropdownBoxProps) {
  const selectedTeammates = chain(props.selectedTeammateIds)
    .map((selectedTeammate) => teammates.find((teammate) => teammate.id === selectedTeammate))
    .compact()
    .value();

  return (
    <StyledTagDiv>
      <StyledFadedDiv>{renderTeammateValue(teammates, selectedTeammates)}</StyledFadedDiv>
    </StyledTagDiv>
  );
}

/* Render the current button placeholder, determined by the number of total teammates and currently selected teammates. */
function renderTeammateValue(
  teammates: ReadonlyArray<TeammateModel>,
  selectedTeammates: ReadonlyArray<TeammateModel>,
) {
  if (!selectedTeammates.length) {
    return <Trans id="pick-teammate-workspaces-placeholder-none">Select teammates</Trans>;
  }

  if (selectedTeammates.length === teammates.length) {
    return (
      <StyledAllTeammatesSpan>
        <Trans id="pick-teammate-workspaces-placeholder-all">All teammates</Trans>
      </StyledAllTeammatesSpan>
    );
  }

  if (selectedTeammates.length === 1) {
    return renderTeammateValueOne(selectedTeammates[0]);
  }

  if (selectedTeammates.length === 2) {
    return renderTeammateValueTwo(selectedTeammates[0], selectedTeammates[1]);
  }

  const remainder = selectedTeammates.length - 2;

  return renderTeammateValueMore(selectedTeammates[0], selectedTeammates[1], remainder);
}

function renderTeammateValueOne(teammate: TeammateModel) {
  return <Trans id="pick-teammate-workspaces-placeholder-one">{renderTeammateItem(teammate)}</Trans>;
}

function renderTeammateValueTwo(teammate1: TeammateModel, teammate2: TeammateModel) {
  return (
    <StyledTeammatesContainerDiv>
      <StyledTeammateItemsWrapperDiv>
        <Trans id="pick-teammate-workspaces-placeholder-two">
          {renderTeammateItem(teammate1)},{renderTeammateItem(teammate2)}
        </Trans>
      </StyledTeammateItemsWrapperDiv>
    </StyledTeammatesContainerDiv>
  );
}

function renderTeammateValueMore(teammate1: TeammateModel, teammate2: TeammateModel, remainder: number) {
  return (
    <StyledTeammatesContainerDiv>
      <Trans id="pick-teammate-workspaces-placeholder-more">
        <StyledTeammateItemsWrapperDiv>
          {renderTeammateItem(teammate1)},{renderTeammateItem(teammate2)}
        </StyledTeammateItemsWrapperDiv>
        ,<StyledTeammateRemainderDiv>+{remainder}</StyledTeammateRemainderDiv>
      </Trans>
    </StyledTeammatesContainerDiv>
  );
}

function renderTeammateItem(teammate: TeammateModel) {
  return (
    <StyledTeammateItemDiv>
      <StyledTeammateAvatarDiv>
        <TeammateAvatar teammate={teammate} size={16} />
      </StyledTeammateAvatarDiv>
      {buildTeammateDisplayName(teammate)}
    </StyledTeammateItemDiv>
  );
}

function renderDropdown(
  teammates: ReadonlyArray<TeammateModel>,
  isCheckedMap: IsCheckedMap,
  popoverProps: PopoverTransferProps,
  props: PickTeammateWorkspacesDropdownBoxProps,
) {
  const renderRow = (teammate: TeammateModel, index: number) =>
    renderTeammateRow(isCheckedMap, teammates, teammate, index, props);

  const dropdownSource = new SyncDropdownSource({
    items: teammates,
    rowFilter: filterRows,
    renderRow,
  });

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      interactionId="pick-teammate-workspaces-dropdown"
      title={<Trans id="pick-teammate-workspaces-title">Teammates</Trans>}
      width={300}
      source={dropdownSource}
      rightIcon={renderRightIcon(teammates, props)}
    />
  );
}

function renderTeammateRow(
  isCheckedMap: IsCheckedMap,
  teammates: ReadonlyArray<TeammateModel>,
  teammate: TeammateModel,
  index: number,
  props: PickTeammateWorkspacesDropdownBoxProps,
) {
  if (!teammate) {
    return null;
  }

  const isChecked = Boolean(isCheckedMap.get(teammate.id));

  const onTeammateSelect: HandlerOf<FrontEvent> = (e) => {
    e.preventDefault(); // Keep the dropdown open

    const newTeammateIds = isChecked
      ? props.selectedTeammateIds.filter((teammateId) => teammateId !== teammate.id)
      : [...props.selectedTeammateIds, teammate.id];

    props.onSelectedTeammatesChange(newTeammateIds);
  };

  return (
    <DropdownItem
      index={index}
      onSelect={onTeammateSelect}
      renderRightContent={() => <DropdownItemCheckbox isChecked={isChecked} />}
    >
      <DropdownItemTeammateAvatar teammate={teammate} />
      <DropdownItemContent isChecked={isChecked}>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
    </DropdownItem>
  );
}

function renderRightIcon(
  teammates: ReadonlyArray<TeammateModel>,
  props: PickTeammateWorkspacesDropdownBoxProps,
) {
  const {selectedTeammateIds} = props;

  if (selectedTeammateIds.length === teammates.length) {
    return renderHideAll(props);
  }

  return renderSelectAll(teammates, props);
}

function renderSelectAll(
  teammates: ReadonlyArray<TeammateModel>,
  props: PickTeammateWorkspacesDropdownBoxProps,
) {
  const onRequestSelectAllClick = () => {
    props.onSelectedTeammatesChange(teammates.map((teammate) => teammate.id));
  };

  return (
    <Button size={VisualSizesEnum.SMALL} variant={ButtonVariantsEnum.LINK} onClick={onRequestSelectAllClick}>
      <Trans id="pick-teammate-workspaces-selectall">Select all</Trans>
    </Button>
  );
}

function renderHideAll(props: PickTeammateWorkspacesDropdownBoxProps) {
  const onRequestHideAllClick = () => {
    props.onSelectedTeammatesChange([]);
  };

  return (
    <Button size={VisualSizesEnum.SMALL} variant={ButtonVariantsEnum.LINK} onClick={onRequestHideAllClick}>
      <Trans id="pick-teammate-workspaces-unpinall">Hide all</Trans>
    </Button>
  );
}

/*
 * Helpers.
 */

const filterRows = memoize(20)((teammates: ReadonlyArray<TeammateModel>, query: string) => {
  // Shortcut for empty query.
  if (!query) {
    return teammates;
  }

  // Otherwise, filter by name.
  return teammates.filter((t) => doesTeammateMatchQuery(t, query));
});

function computeNamespaceTeammates(
  teammates: ReadonlyArray<TeammateModel>,
  namespace: string,
): ReadonlyArray<TeammateModel> {
  return teammates.filter((teammate) => findParticipatingNamespaces(teammate).includes(namespace));
}

function computeIsCheckedMapKey(teammate: TeammateModel) {
  return teammate.id;
}
